<template>
  <clinic-layout title="Add appointment" :subtitle="subtitle">
    <div v-if="!doctor.doctorId" class="adm-alert adm-alert--warning">
      <i class="fas fa-exclamation-triangle"></i>
      <div>
        Choose a doctor first.
        <router-link to="/clinic-dashboard" class="add-appt__link">Go to doctors</router-link>
      </div>
    </div>

    <template v-else>
      <ol class="add-appt__steps" aria-label="Steps">
        <li class="is-current"><span>1</span> Find the patient</li>
        <li><span>2</span> Pick date &amp; time</li>
      </ol>

      <div class="adm-card">
        <div class="adm-card__body">
          <form class="add-appt__search" @submit.prevent="search(1)">
            <div class="adm-search add-appt__search-box">
              <i class="fas fa-search"></i>
              <input ref="query" v-model="query" class="adm-input" autocomplete="off"
                     placeholder="Name, full mobile number or email" aria-label="Search patients" />
            </div>
            <button type="submit" class="adm-btn adm-btn--primary" :disabled="loading">
              <span v-if="loading" class="adm-spinner"></span> Search
            </button>
            <button type="button" class="adm-btn" @click="openRegister()">
              <i class="fas fa-user-plus"></i> New patient
            </button>
          </form>
          <p class="adm-field__help add-appt__hint">
            Searching by <strong>{{ searchKindLabel }}</strong>.
            Mobile numbers need all the digits; numbers without a country code are treated as +91.
          </p>
        </div>

        <div v-if="error" class="adm-alert adm-alert--error add-appt__inset"><i class="fas fa-exclamation-circle"></i><div>{{ error }}</div></div>

        <template v-if="searched">
          <div v-if="loading && !patients.length">
            <div v-for="n in 3" :key="n" class="adm-list-row"><div class="adm-skeleton" style="width: 50%"></div></div>
          </div>
          <div v-else-if="!patients.length" class="adm-empty">
            <i class="fas fa-user-slash"></i>
            <strong>No patient found</strong>
            Check the spelling or number, or register them as a new patient.
            <div class="add-appt__empty-action">
              <button type="button" class="adm-btn adm-btn--primary" @click="openRegister()">
                <i class="fas fa-user-plus"></i> Register new patient
              </button>
            </div>
          </div>
          <template v-else>
            <div class="adm-table-wrap">
              <table class="adm-table">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>Mobile</th>
                    <th>Email</th>
                    <th class="adm-col-actions"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="patient in patients" :key="patient.patientid">
                    <td>
                      <div class="adm-person">
                        <span class="adm-avatar adm-avatar--sm">{{ initials(fullName(patient)) }}</span>
                        <span>
                          <span class="adm-person__name">{{ fullName(patient) }}</span>
                          <span class="adm-person__meta">ID {{ patient.patientid }}{{ details(patient) }}</span>
                        </span>
                      </div>
                    </td>
                    <td class="adm-nowrap">{{ patient.contactnumbers || "—" }}</td>
                    <td class="add-appt__email">{{ patient.email || "—" }}</td>
                    <td class="adm-col-actions">
                      <button type="button" class="adm-btn adm-btn--sm adm-btn--primary" @click="book(patient.patientid)">
                        Book <i class="fas fa-arrow-right"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="adm-pagination">
              <span>
                Showing {{ (pagination.page - 1) * pagination.per_page + 1 }}–{{ Math.min(pagination.page * pagination.per_page, pagination.total) }}
                of {{ pagination.total }}
              </span>
              <div class="adm-pagination__pages">
                <button type="button" class="adm-btn adm-btn--sm" :disabled="loading || !pagination.has_prev" @click="search(pagination.page - 1)">
                  <i class="fas fa-arrow-left"></i> Previous
                </button>
                <button type="button" class="adm-btn adm-btn--sm" :disabled="loading || !pagination.has_next" @click="search(pagination.page + 1)">
                  Next <i class="fas fa-arrow-right"></i>
                </button>
              </div>
            </div>
          </template>
        </template>
      </div>
    </template>

    <!-- Register a new patient -->
    <admin-modal v-if="registering" title="New patient" subtitle="Registers the patient, then continues to booking."
                 :busy="saving" @close="registering = false">
      <div v-if="formError" class="adm-alert adm-alert--error"><i class="fas fa-exclamation-circle"></i><div>{{ formError }}</div></div>

      <div v-if="duplicates.length" class="adm-alert adm-alert--warning add-appt__dupes">
        <i class="fas fa-user-friends"></i>
        <div>
          <strong>{{ duplicates.length === 1 ? "A patient is" : `${duplicates.length} patients are` }} already registered with this mobile number.</strong>
          Book for one of them, or register a new patient anyway.
          <div class="add-appt__dupe-list">
            <button v-for="patient in duplicates.slice(0, 5)" :key="patient.patientid" type="button" class="adm-btn adm-btn--sm"
                    @click="book(patient.patientid)">
              {{ fullName(patient) }} · ID {{ patient.patientid }} <i class="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>

      <div class="adm-grid">
        <div class="adm-field adm-span-all">
          <label class="adm-label" for="np-phone">Mobile number <span class="adm-req">*</span></label>
          <div class="add-appt__phone">
            <select v-model="form.countryCode" class="adm-select add-appt__cc" aria-label="Country code">
              <option v-for="c in countries" :key="c.code" :value="c.dial">{{ c.code }} {{ c.dial }}</option>
            </select>
            <input id="np-phone" v-model="form.phone" class="adm-input" :class="{ 'is-invalid': errors.phone }"
                   inputmode="numeric" maxlength="15" placeholder="9876543210" @input="duplicates = []" />
          </div>
          <div v-if="errors.phone" class="adm-field__error">{{ errors.phone }}</div>
        </div>
        <div class="adm-field">
          <label class="adm-label" for="np-first">First name <span class="adm-req">*</span></label>
          <input id="np-first" v-model="form.firstName" class="adm-input" :class="{ 'is-invalid': errors.firstName }" maxlength="45" />
          <div v-if="errors.firstName" class="adm-field__error">{{ errors.firstName }}</div>
        </div>
        <div class="adm-field">
          <label class="adm-label" for="np-last">Last name</label>
          <input id="np-last" v-model="form.lastName" class="adm-input" :class="{ 'is-invalid': errors.lastName }" maxlength="45" />
          <div v-if="errors.lastName" class="adm-field__error">{{ errors.lastName }}</div>
        </div>
        <div class="adm-field">
          <span class="adm-label">Gender <span class="adm-req">*</span></span>
          <div class="adm-tabs add-appt__gender" role="radiogroup" aria-label="Gender">
            <button v-for="g in genders" :key="g.value" type="button" class="adm-tab" role="radio"
                    :class="{ 'is-active': form.gender === g.value }" :aria-checked="form.gender === g.value"
                    @click="form.gender = g.value">{{ g.label }}</button>
          </div>
          <div v-if="errors.gender" class="adm-field__error">{{ errors.gender }}</div>
        </div>
        <div class="adm-field">
          <label class="adm-label" for="np-age">Age</label>
          <input id="np-age" v-model="form.age" class="adm-input" :class="{ 'is-invalid': errors.age }" inputmode="numeric" maxlength="3" />
          <div v-if="errors.age" class="adm-field__error">{{ errors.age }}</div>
        </div>
        <div class="adm-field adm-span-all">
          <label class="adm-label" for="np-email">Email</label>
          <input id="np-email" v-model="form.email" type="email" class="adm-input" :class="{ 'is-invalid': errors.email }" maxlength="45" />
          <div v-if="errors.email" class="adm-field__error">{{ errors.email }}</div>
        </div>
      </div>

      <template #footer>
        <button type="button" class="adm-btn" :disabled="saving" @click="registering = false">Cancel</button>
        <button type="button" class="adm-btn adm-btn--primary" :disabled="saving" @click="register">
          <span v-if="saving" class="adm-spinner"></span>
          {{ duplicates.length ? "Register anyway" : "Register & continue" }} <i class="fas fa-arrow-right"></i>
        </button>
      </template>
    </admin-modal>
  </clinic-layout>
</template>

<script>
import axios from "axios";
import ClinicLayout from "./components/ClinicLayout.vue";
import AdminModal from "../admin/components/AdminModal.vue";
import { EMAIL_RE, errorMessage, initials } from "../admin/admin-utils";

const COUNTRIES = [
  { code: "IN", dial: "+91" }, { code: "AE", dial: "+971" }, { code: "SA", dial: "+966" }, { code: "QA", dial: "+974" },
  { code: "KW", dial: "+965" }, { code: "OM", dial: "+968" }, { code: "BH", dial: "+973" }, { code: "US", dial: "+1" },
  { code: "GB", dial: "+44" }, { code: "AU", dial: "+61" }, { code: "SG", dial: "+65" }, { code: "MY", dial: "+60" },
  { code: "NZ", dial: "+64" },
];
const NAME_RE = /^[A-Za-z][A-Za-z .'-]*$/;

/** What the search box holds: an email, a mobile number (E.164) or a name. */
function classify(text) {
  const value = text.trim();
  if (value.includes("@")) return { kind: "email", params: { email: value } };
  const digits = value.replace(/[\s\-()]/g, "");
  if (/^\+?\d{6,15}$/.test(digits)) {
    let phone = digits;
    if (!phone.startsWith("+")) phone = phone.length === 10 ? `+91${phone}` : `+${phone}`;
    return { kind: "mobile number", params: { contactnumbers: phone } };
  }
  return { kind: "name", params: { patientname: value } };
}

export default {
  name: "add-clinic-appointment",
  components: { ClinicLayout, AdminModal },
  data() {
    return {
      query: "",
      patients: [],
      pagination: { total: 0, page: 1, per_page: 12, has_next: false, has_prev: false },
      loading: false,
      searched: false,
      error: "",
      requestId: 0,
      registering: false,
      saving: false,
      form: {},
      errors: {},
      formError: "",
      duplicates: [],
      countries: COUNTRIES,
      genders: [{ value: "M", label: "Male" }, { value: "F", label: "Female" }, { value: "O", label: "Other" }],
    };
  },
  computed: {
    doctor() {
      return this.$store.getters.getclinicDetails || {};
    },
    subtitle() {
      if (!this.doctor.doctorId) return "Find or register the patient, then pick a slot.";
      const who = this.doctor.doctorName ? `Dr. ${this.doctor.doctorName}` : "the selected doctor";
      return this.doctor.clinicname ? `For ${who} at ${this.doctor.clinicname}` : `For ${who}`;
    },
    searchKindLabel() {
      return this.query.trim() ? classify(this.query).kind : "name, mobile number or email";
    },
  },
  watch: {
    query(value) {
      clearTimeout(this.timer);
      if (value.trim().length >= 3) this.timer = setTimeout(() => this.search(1), 400);
    },
  },
  methods: {
    initials,
    fullName(patient) {
      return [patient.firstname, patient.lastname].filter(Boolean).join(" ").trim() || "Unnamed";
    },
    details(patient) {
      const gender = { M: "Male", F: "Female", O: "Other" }[patient.gender];
      return [gender, patient.age ? `${patient.age} yrs` : ""].filter(Boolean).map(v => ` · ${v}`).join("");
    },
    async search(page = 1) {
      clearTimeout(this.timer);
      const text = this.query.trim();
      if (!text) {
        this.error = "Type a name, mobile number or email to search.";
        return;
      }
      this.error = "";
      this.searched = true;
      this.loading = true;
      const requestId = ++this.requestId;
      try {
        const { data } = await axios.get("patients", { params: { ...classify(text).params, page, per_page: 12 } });
        if (requestId !== this.requestId) return;
        this.patients = data.patients || [];
        this.pagination = data.pagination || this.pagination;
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.patients = [];
        this.error = errorMessage(error, "Could not search patients.");
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    book(patientid) {
      this.$store.dispatch("updateAppointmentDraft", {
        patientid,
        doctorid: this.doctor.doctorId,
        doctorImage: this.doctor.doctorImage,
      });
      this.$router.push({ path: `/bookingclinic/${this.doctor.doctorId}`, query: { from: "ClinicDashboard" } });
    },
    openRegister() {
      // Start the form from whatever was searched for.
      const found = this.query.trim() ? classify(this.query) : { params: {} };
      const phone = found.params.contactnumbers || "";
      const country = COUNTRIES.find(c => phone.startsWith(c.dial)) || COUNTRIES[0];
      const [firstName = "", ...rest] = (found.params.patientname || "").split(/\s+/);
      this.form = {
        countryCode: phone ? country.dial : "+91",
        phone: phone ? phone.slice(country.dial.length) : "",
        firstName: firstName ? firstName[0].toUpperCase() + firstName.slice(1) : "",
        lastName: rest.join(" "),
        gender: "",
        age: "",
        email: found.params.email || "",
      };
      this.errors = {};
      this.formError = "";
      this.duplicates = [];
      this.registering = true;
    },
    validate() {
      const f = this.form;
      const errors = {};
      const digits = f.phone.replace(/\D/g, "");
      if (f.countryCode === "+91" ? !/^[6-9]\d{9}$/.test(digits) : !/^\d{6,12}$/.test(digits)) {
        errors.phone = f.countryCode === "+91" ? "Enter a 10-digit Indian mobile number." : "Enter a valid mobile number.";
      }
      if (!f.firstName.trim() || !NAME_RE.test(f.firstName.trim())) errors.firstName = "Enter the first name (letters only).";
      if (f.lastName.trim() && !NAME_RE.test(f.lastName.trim())) errors.lastName = "Use letters only.";
      if (!f.gender) errors.gender = "Choose a gender.";
      if (f.age && !(/^\d{1,3}$/.test(f.age) && Number(f.age) <= 120)) errors.age = "Enter an age between 0 and 120.";
      if (f.email.trim() && !EMAIL_RE.test(f.email.trim())) errors.email = "Enter a valid email.";
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    async register() {
      if (!this.validate()) return;
      const phone = `${this.form.countryCode}${this.form.phone.replace(/\D/g, "")}`;
      this.formError = "";
      this.saving = true;
      try {
        if (!this.duplicates.length) {
          // Warn once if this number is already registered.
          const { data } = await axios.get("patients", { params: { contactnumbers: phone, per_page: 5 } });
          if ((data.patients || []).length) {
            this.duplicates = data.patients;
            return;
          }
        }
        const { data } = await axios.post("/patients", {
          patientid: 0,
          firstname: this.form.firstName.trim(),
          lastname: this.form.lastName.trim(),
          contactnumbers: phone,
          email: this.form.email.trim() || null,
          gender: this.form.gender,
          age: this.form.age || null,
          listyn: true,
        });
        this.registering = false;
        this.book(data.patientid);
      } catch (error) {
        this.formError = errorMessage(error, "Could not register the patient.");
      } finally {
        this.saving = false;
      }
    },
  },
  mounted() {
    if (this.$refs.query) this.$refs.query.focus();
  },
  beforeUnmount() {
    clearTimeout(this.timer);
  },
};
</script>

<style scoped>
.add-appt__steps {
  display: flex;
  gap: 24px;
  list-style: none;
  padding: 0;
  margin: 0 0 16px;
  font-size: 13px;
  font-weight: 600;
  color: var(--adm-muted);
}

.add-appt__steps li {
  display: flex;
  align-items: center;
  gap: 8px;
}

.add-appt__steps span {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  background: var(--adm-line);
  color: var(--adm-ink-2);
}

.add-appt__steps .is-current {
  color: var(--adm-ink);
}

.add-appt__steps .is-current span {
  background: var(--adm-primary);
  color: #fff;
}

.add-appt__search {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.add-appt__search-box {
  flex: 1 1 320px;
  max-width: none;
}

.add-appt__hint {
  margin: 8px 0 0;
}

.add-appt__inset {
  margin: 0 20px 16px;
}

.add-appt__email {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.add-appt__empty-action {
  margin-top: 16px;
}

.add-appt__link {
  font-weight: 700;
  color: inherit;
  text-decoration: underline;
  margin-left: 4px;
}

.add-appt__phone {
  display: flex;
  gap: 8px;
}

.add-appt__cc {
  width: 120px;
  flex-shrink: 0;
}

.add-appt__gender {
  display: flex;
}

.add-appt__gender .adm-tab {
  flex: 1;
  justify-content: center;
}

.add-appt__dupes strong {
  display: block;
  margin-bottom: 2px;
}

.add-appt__dupe-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}
</style>
